import { Theme } from "@mui/material";
import { brown } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { enumKeyOf, enumKeys } from "_shared/_utilities/enum-utils";
import Guid from "_shared/guids/guid";
import { DocumentError } from "documents/_stores/documents-redux-slice";
import Document from "documents/document";
import UnavailableDocument from "documents/types/unavailable-documents/unavailable-document";
import { EntityAgreementError } from "legal-entities/agreements/_stores/entity-agreements-redux-slice";
import EntityAgreement from "legal-entities/agreements/entity-agreement";
import Individual from "marketplace/individuals/individual";
import { ForumError } from "messaging/forums/_stores/forums-redux-slice";
import Forum from "messaging/forums/forum";
import Permission from "permissions/permission";
import PermissionTemplate from "permissions/templates/permission-template";
import PermissionsByGrantee from "permissions/by-grantee/permissions-by-grantee";
import {
  DocumentAccess,
  DocumentContentsAccess,
  GeneralDocumentAccess,
} from "permissions/resources/documents/documents-permissions-resources";
import {
  EntityAgreementAccess,
  LegalEntityAccess,
} from "permissions/resources/legal-entities/legal-entities-permissions-resources";
import { CommunicationsAccess, ForumAccess } from "permissions/resources/messaging/messaging-permissions-resources";
import type { Resource } from "permissions/resources/permission-resource";
import { 
  AccessLevel, 
  ResourceConstructor, 
  ChildResourceType, 
  Scope, 
  ResourceType
} from "permissions/resources/types";
import {
  ContractsAccess,
  CurrencyFieldsAccess,
  DiscountsAccess,
  FeeSchedulesAccess,
  FeeScheduleTemplateAccess,
  InvoicesAccess,
  ProposalAccess,
  ProposalRequestAccess,
  ProposalsAccess,
  TeamsAccess,
  TeamTemplateAccess,
  WorkAgreementAccess,
  WorkAgreementsAccess,
} from "permissions/resources/work/work-permissions-resources";
import {
  AccessTypeEnumByResourceType,
  CardMeta,
  CardStyle,
  CATEGORY_OPTIONS,
  CategoryLabel,
  ChildResourceConstructorMap,
  ControlMeta,
  MetaControlType,
  TableRow,
} from "permissions/_views/types/permission-types";
import BarebonesProposalResponse from "work/proposals/_api/responses/barebones-proposal-response";
import { ProposalError } from "work/proposals/_store/proposals-redux-slice";
import BarebonesWorkAgreementInfoAPIResponse from "work/work-agreements/_api/responses/barebones-work-agreement-api-response";
import { WorkAgreementError } from "work/work-agreements/_store/work-agreement-redux-slice";
import { LoginAccess, UserNetworkingAccess } from "permissions/resources/users/users-permissions-resources";
import { NotificationsAccess } from "permissions/resources/notifications/notifications-permissions-resources";
import { MarketplaceAccess, MarketplaceSearchAccess } from "permissions/resources/marketplace/marketplace-permissions-resources";
import { KnowledgeBaseAccess } from "permissions/resources/knowledge-base/knowledge-base-permissions-resources";

export function getResourceBaseColor(resource?: Resource): string {
  if (!resource) return brown[800];
  const t = resource.resourceType;
  const match = CATEGORY_OPTIONS.find((o) => o.regex.test(t));
  return match?.color ?? brown[800];
}

export function getCardStyle(
  theme: Theme,
  resource: Resource,
  disabled: boolean,
  activeCategory: CategoryLabel | ""
): CardStyle {
  const base = disabled ? theme.palette.grey[500] : getResourceBaseColor(resource);
  const border = alpha(base, 0.75);
  const matchesActiveCategory: boolean =
    CATEGORY_OPTIONS.find((o) => o.regex.test(resource.resourceType))?.label === activeCategory;
  return {
    baseColor: base,
    textColor: base,
    bgColor: alpha(base, 0.05),
    borderColor: border,
    display: matchesActiveCategory || !activeCategory ? "flex" : "none",
    selectSx: {
      "& .MuiOutlinedInput-notchedOutline": { borderColor: border },
      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: alpha(border, 0.5) },
      "& .MuiSelect-outlined": { backgroundColor: alpha(base, 0.05) },
    },
  };
}

export const selectEffectivePermission = (perms: Permission[]): Permission | undefined => {
  if (!perms.length) return undefined;

  return perms[0];
  /*
  const rankAccess = (p: Permission): number => {
    if (!("accessLevel" in p.resource)) return 0;
    const v = (p.resource as { accessLevel: AccessLevel | string | number | undefined }).accessLevel;
    if (v == null) return 0;
    if (typeof v === "number") return v;
    const key = enumKeyOf(AccessLevel, v);
    const idx = enumKeys(AccessLevel).indexOf(key);
    return Math.max(idx, 0);
  };
  const enabled: Permission[] = perms.filter((p) => !p.disabledDate);
  const pool: Permission[] = enabled.length ? enabled : perms;

  pool.sort((a, b) => rankAccess(b) - rankAccess(a));

  const getTimestamp = (p: Permission): number => p.createdDate?.value ?? 0;
  pool.sort((a, b) => {
    const r = rankAccess(b) - rankAccess(a);
    if (r !== 0) return r;
    return getTimestamp(b) - getTimestamp(a);
  });

  return pool[0];
  */
};
function humanizePascalCase(input: string): string {
  return input
    .replaceAll(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replaceAll(/Access$/g, "")
    .trim();
}

const CtorToResourceType = new Map<ResourceConstructor, ResourceType>([
  // Authentication
  [LoginAccess, ResourceType.LoginAccess],

  // Marketplace
  [MarketplaceAccess, ResourceType.MarketplaceAccess],
  [MarketplaceSearchAccess, ResourceType.MarketplaceSearchAccess],
  
  // Documents
  [GeneralDocumentAccess, ResourceType.GeneralDocumentAccess],
  [DocumentAccess, ResourceType.DocumentAccess],
  [DocumentContentsAccess, ResourceType.DocumentContentsAccess],

  // Legal Entities
  [LegalEntityAccess, ResourceType.LegalEntityAccess],
  [EntityAgreementAccess, ResourceType.EntityAgreementAccess],
  [ContractsAccess, ResourceType.ContractsAccess],
  
  // Messaging, Notifications, Networking
  [ForumAccess, ResourceType.ForumAccess],
  [CommunicationsAccess, ResourceType.CommunicationsAccess],
  [NotificationsAccess, ResourceType.NotificationsAccess],
  [UserNetworkingAccess, ResourceType.UserNetworkingAccess],

  // Proposals
  [ProposalAccess, ResourceType.ProposalAccess],
  [ProposalsAccess, ResourceType.ProposalsAccess],
  [ProposalRequestAccess, ResourceType.ProposalRequestAccess],

  // Work Agreements
  [WorkAgreementAccess, ResourceType.WorkAgreementAccess],
  [WorkAgreementsAccess, ResourceType.WorkAgreementsAccess],
  
  // Financial
  [CurrencyFieldsAccess, ResourceType.CurrencyFieldsAccess],
  [DiscountsAccess, ResourceType.DiscountsAccess],
  [InvoicesAccess, ResourceType.InvoicesAccess],
  [FeeSchedulesAccess, ResourceType.FeeSchedulesAccess],
  [FeeScheduleTemplateAccess, ResourceType.FeeScheduleTemplateAccess],

  // Teams
  [TeamsAccess, ResourceType.TeamsAccess],
  [TeamTemplateAccess, ResourceType.TeamTemplateAccess],

  // Knowldge Base
  [KnowledgeBaseAccess, ResourceType.KnowledgeBaseAccess],
]);

export function resourceTypeFromCtor(Ctor: ResourceConstructor): ResourceType {
  if (!CtorToResourceType.has(Ctor)) {
    throw new Error(`Unknown ResourceConstructor in resourceTypeFromCtor: ${Ctor.name}`);
  }
  return CtorToResourceType.get(Ctor) as ResourceType;
}

/**
 * Human-Readable ResourceType (override only what you want, the rest are generated)
 */
const ResourceTypeLabels: Partial<Record<ResourceType, string>> = {
  [ResourceType.DocumentContentsAccess]: "Document Subject Matter",
  [ResourceType.LegalEntityAccess]: "Legal Entities",
  [ResourceType.GeneralDocumentAccess]: "General Documents",
  [ResourceType.DocumentAccess]: "Documents",
};

export function labelFromResourceType(rt: ResourceType): string {
  return ResourceTypeLabels[rt] ?? humanizePascalCase(rt);
}

export const makeVirtualPermission = (granteeId: Guid, resource: Resource): Permission =>
  new Permission({ resource, entityId: Guid.EMPTY, granteeUserId: granteeId, grantorUserId: Guid.EMPTY });

/**
 * @description Get class name from resource
 * @param resource - The resource constructor
 * @returns The class name of the resource
 */
export const classNameFromResource = (Ctor: ResourceConstructor): string =>
  labelFromResourceType(resourceTypeFromCtor(Ctor));

export const isTopLevelParent = (p: Permission) =>
  !p.parentPermissions?.length &&
  !("documentId" in p.resource && p.resource.documentId) &&
  !("forumId" in p.resource && p.resource.forumId) &&
  !("proposalId" in p.resource && p.resource.proposalId) &&
  !("templateId" in p.resource && p.resource.templateId);

function loadName(
  id: Guid | undefined,
  {
    isLoading,
    error,
    pick,
  }: {
    isLoading: (id: Guid) => boolean;
    error: (id: Guid) => string | null;
    pick: (id: Guid) => string | undefined;
  },
  fallback: string
): string {
  if (!id) return fallback;
  if (isLoading(id)) return "(Loading...)";
  const err = error(id);
  if (err) return `(Error: ${err})`;
  return pick(id) ?? fallback;
}

/**
 * Get the name of the user who granted the permission.
 * @param permission The permission object.
 * @param individuals List of individuals to look up the grantor's name.
 * @param loadingIndividualUserIds List of user IDs that are currently loading.
 * @returns The name of the grantor user or a placeholder if loading or unknown.
 */
export const getGrantorName = (
  permission: Permission,
  individuals: Individual[],
  loadingIndividualUserIds: Guid[]
): string => {
  if (!permission.grantorUserId) return "Unknown User";
  if (permission.grantorUserId.isEqualTo(Guid.EMPTY)) return "System";
  const grantorName = individuals.find((individual) => individual.userId.isEqualTo(permission.grantorUserId))?.fullName;
  const isLoadingGrantor = loadingIndividualUserIds.some((id) => id.isEqualTo(permission.grantorUserId));
  if (isLoadingGrantor) return "(Loading...)";
  return grantorName ?? "Unknown User";
};

export function getProposalName(
  resource: ProposalAccess,
  loadingProposalIds: Guid[] | boolean,
  allProposalErrors: ProposalError[],
  barebonesProposals: BarebonesProposalResponse[]
): string {
  return loadName(
    resource.proposalId,
    {
      isLoading: (id) => typeof loadingProposalIds === "boolean" ? loadingProposalIds : loadingProposalIds.some((loadingId) => loadingId.isEqualTo(id)),
      error: (id) => allProposalErrors.find((error) => error.id.isEqualTo(id))?.error?.message || null,
      pick: (id) => barebonesProposals?.find((proposal) => proposal?.id === id.value)?.name,
    },
    "Unknown Proposal"
  );
}

export function getWorkAgreementName(
  resource: WorkAgreementAccess,
  loadingWorkAgreementIds: Guid[] | boolean,
  allWorkAgreementErrors: WorkAgreementError[],
  barebonesWorkAgreements: BarebonesWorkAgreementInfoAPIResponse[]
): string {
  return loadName(
    resource.workAgreementId,
    {
      isLoading: (id) => typeof loadingWorkAgreementIds === "boolean" ? loadingWorkAgreementIds : loadingWorkAgreementIds.some((loadingId) => loadingId.isEqualTo(id)),
      error: (id) => allWorkAgreementErrors.find((error) => error.id.isEqualTo(id))?.error?.message || null,
      pick: (id) => barebonesWorkAgreements?.find((wa) => wa?.id === id.value)?.name,
    },
    "Unknown Work Agreement"
  );
}

export function getEntityAgreementName(
  resource: EntityAgreementAccess,
  loadingEntityAgreementIds: (Guid | null | undefined)[] | boolean,
  allEntityAgreementErrors: EntityAgreementError[],
  entityAgreements: EntityAgreement[]
): string {
  return loadName(
    resource.entityAgreementId,
    {
      isLoading: (id) => typeof loadingEntityAgreementIds === "boolean" ? loadingEntityAgreementIds : loadingEntityAgreementIds.some((loadingId) => loadingId?.isEqualTo(id)),
      error: (id) => allEntityAgreementErrors.find((error) => error.id.isEqualTo(id))?.error?.message || null,
      pick: (id) => entityAgreements?.find((ea) => ea?.id.isEqualTo(id))?.entityName?.value,
    },
    "Unknown Entity Agreement"
  );
}

export function getDocumentName(
  resource: DocumentAccess,
  loadingDocumentIds: Guid[] | boolean,
  allDocumentErrors: DocumentError[],
  documents: Array<Document | UnavailableDocument>
): string {
  return loadName(
    resource.documentId,
    {
      isLoading: (id) => typeof loadingDocumentIds === "boolean" ? loadingDocumentIds : loadingDocumentIds.some((loadingId) => loadingId.isEqualTo(id)),
      error: (id) => allDocumentErrors.find((error) => error.id.isEqualTo(id))?.error || null,
      pick: (id) => {
        const doc = documents.find((doc) => doc.id.isEqualTo(id));
        if (!doc) return undefined;
        if (doc instanceof Document) {
          return doc.name?.value;
        }
        return "Unavailable Document";
      },
    },
    "Unknown Document"
  );
}

export function getForumName(
  resource: ForumAccess,
  loadingForumIds: Guid[] | boolean,
  allForumErrors: ForumError[],
  forums: Forum[]
): string {
  return loadName(
    resource.forumId,
    {
      isLoading: (id) => typeof loadingForumIds === "boolean" ? loadingForumIds : loadingForumIds.some((loadingId) => loadingId.isEqualTo(id)),
      error: (id) => allForumErrors.find((error) => error.id.isEqualTo(id))?.error || null,
      pick: (id) => forums.find((forum) => forum?.id?.isEqualTo(id))?.name.toString(),
    },
    "Unknown Forum"
  );
}

export function getControlMeta(resource: Resource, controlType: MetaControlType): ControlMeta | null {
  if ("accessLevel" in resource && controlType === MetaControlType.AccessLevel) {
    return {
      type: MetaControlType.AccessLevel,
      label: "Access Level",
      options: enumKeys(AccessLevel),
      value: enumKeyOf(AccessLevel, resource.accessLevel),
    };
  }
  if ("scope" in resource && controlType === MetaControlType.Scope) {
    return {
      type: MetaControlType.Scope,
      label: "Scope",
      options: enumKeys(Scope),
      value: enumKeyOf(Scope, resource.scope),
    };
  }
  if ("accessType" in resource && controlType === MetaControlType.AccessType) {
    const en = AccessTypeEnumByResourceType[resource.resourceType as keyof typeof AccessTypeEnumByResourceType];
    if (!en) return null;
    return {
      type: MetaControlType.AccessType,
      label: "Access Type",
      options: enumKeys(en),
      value: enumKeyOf(en, resource.accessType),
    };
  }
  return null;
}

export const toCardMeta = (permission: Permission, key: string, control: ControlMeta | null): CardMeta => ({
  key,
  permission,
  resource: permission.resource,
  isVirtual: !permission.id,
  control,
});

export const toTemplateCardMeta = (resource: Resource, key: string, control: ControlMeta | null): CardMeta => ({
  key,
  resource,
  control,
});

export function clearPermissionFilters(search: string): string {
  const params = new URLSearchParams(search);
  const KEYS = [
    "granteeUserId",
    "granteeUserIds",
    "grantorUserId",
    "resource",
    "minCreatedDate",
    "maxCreatedDate",
    "minDisabledDate",
    "maxDisabledDate",
    "categories",
    "category",
    "accessType",
    "childAccessTypes",
    "hasParent",
    "documentIds",
    "proposalIds",
    "workAgreementIds",
  ];
  for (const k of KEYS) params.delete(k);
  return params.toString();
}

export function childResourceConstructors(): ReadonlyArray<ResourceConstructor> {
  const ChildTypeToConstructor: ChildResourceConstructorMap = {
    DocumentAccess,
    EntityAgreementAccess,
    ForumAccess,
    ProposalAccess,
    WorkAgreementAccess,
    ProposalRequestAccess,
    FeeScheduleTemplateAccess,
    TeamTemplateAccess,
  } as const;
  return (
    Object.keys(ChildResourceType).filter((k) => Number.isNaN(Number(k))) as Array<keyof typeof ChildResourceType>
  ).map((k) => ChildTypeToConstructor[k]);
}

export const columnIdForTopLevel = (resource: ResourceConstructor) => {
  if (resource === DocumentContentsAccess) return "group:DocumentContents";
  if (resource === LegalEntityAccess) return "group:LegalEntities";

  const resourceType = resourceTypeFromCtor(resource);
  return `rt:${resourceType}`;
};

export function sumPermissionCounts(resp?: PaginatedResponse<PermissionsByGrantee> | undefined) {
  const totals = new Map<string, number>();
  for (const row of resp?.data ?? []) {
    for (const [k, v] of Object.entries(row.permissionCounts ?? {})) {
      totals.set(k, (totals.get(k) ?? 0) + (v ?? 0));
    }
  }
  return totals;
}

export function computeTopLevelVisibility(
  topLevelResources: ResourceConstructor[],
  totals: Map<string, number>,
  { hasParent, hasAdditionalFilters }: { hasParent: boolean; hasAdditionalFilters: boolean }
) {
  const next: Record<string, boolean> = {};
  const suppressTop = hasParent || hasAdditionalFilters;

  for (const resource of topLevelResources) {
    const id = columnIdForTopLevel(resource);
    const resourceType = resourceTypeFromCtor(resource);
    const total = totals.get(resourceType) ?? 0;

    next[id] = suppressTop ? false : total > 0;
  }

  if (!suppressTop) {
    next["group:DocumentContents"] = true;
  }

  return next;
}

export function computeChildVisibility(childTypesInData: ResourceConstructor[], allow: boolean) {
  const CHILD_ID_BY_RT: Partial<Record<ResourceType, string>> = {
    [ResourceType.ProposalAccess]: "group:Proposal",
    [ResourceType.WorkAgreementAccess]: "group:WorkAgreement",
    [ResourceType.LegalEntityAccess]: "group:LegalEntities",
    [ResourceType.DocumentContentsAccess]: "group:DocumentContents",
    [ResourceType.DocumentAccess]: "rt:DocumentAccess",
  };

  const next: Record<string, boolean> = {};
  for (const Ctor of childTypesInData) {
    const rt = resourceTypeFromCtor(Ctor);
    const id = CHILD_ID_BY_RT[rt] ?? `rt:${rt}`;
    next[id] = allow;
  }
  return next;
}

export function clampByCategory(
  vis: Record<string, boolean>,
  headersById: Map<string, string>,
  activeCategory: CategoryLabel | ""
) {
  if (!activeCategory) return vis;
  const { regex } = CATEGORY_OPTIONS.find((o) => o.label === activeCategory)!;
  const next: Record<string, boolean> = {};
  for (const [id, allowed] of Object.entries(vis)) {
    if (id === "grantee") {
      next[id] = true;
      continue;
    }
    if (!allowed) continue;
    const header = headersById.get(id) ?? "";
    next[id] = regex.test(header) || regex.test(id);
  }
  return next;
}

export const isPermissionsRow = (r: TableRow | null): r is PermissionsByGrantee =>
  (r as PermissionsByGrantee).granteeUserId !== undefined;

export const isTemplateRow = (r: TableRow | null): r is PermissionTemplate =>
  (r as PermissionTemplate).name !== undefined && (r as PermissionsByGrantee).granteeUserId === undefined;
